/**
 * Home component - Version A: Clean v1.5.7 UI + DATABASE-BASED joined events
 * RESTORED: Clean, minimal design you preferred
 * ENHANCED: Database-based joined events (from event.participatingUsers)
 * UPDATED: Category filter as dropdown (not chips)
 * FIXED: Check event's participatingUsers array instead of stale Redux joinedEvents
 */

import { Box, Grid, Button, Card, CardContent, Container, Typography, Slider, Chip, FormControl, InputLabel, Select, MenuItem, Alert } from '@mui/material';
import StraightenIcon from '@mui/icons-material/Straighten';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  loadRadiusPreference,
  saveRadiusPreference
} from '../utils/radiusPersistence';
import { fetchEventsWithLocation, fetchCategories, fetchItemsForEvent, Event as EventType } from '../services/apiService';
import { buildEventsApiUrl } from '../config/api';
import { normalizeImageUrls } from '../utils/images';
import { RootState } from '../store/store';
import { getAuthUserId } from '../utils/authUser';
import { useLayoutContext } from './Layout';

export default function Home() {
  const navigate = useNavigate();
  const { searchQuery, userLocation, searchRadius, setSearchRadius } = useLayoutContext();
  
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  
  const [events, setEvents] = useState<EventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [availableCategories, setAvailableCategories] = useState<string[]>(['all']);
  const [eventCardImages, setEventCardImages] = useState<Record<string, string[]>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEvents, setTotalEvents] = useState(0);
  const [eventsPerPage] = useState(12);
  const [radiusDraft, setRadiusDraft] = useState(() => {
    try {
      return loadRadiusPreference();
    } catch (error) {
      return 25;
    }
  });
  const [emailAlertSubscription, setEmailAlertSubscription] = useState<any>(null);
  const [emailAlertLoading, setEmailAlertLoading] = useState(false);
  const [emailAlertBusy, setEmailAlertBusy] = useState(false);
  const [emailAlertError, setEmailAlertError] = useState<string | null>(null);
  const [emailAlertMessage, setEmailAlertMessage] = useState<string | null>(null);

  const currentUserId = getAuthUserId(user);

  const isEventOwner = (event: EventType | undefined): boolean => {
    if (!isAuthenticated || !user || !event) return false;
    const userId = currentUserId;
    if (!userId) return false;
    const eventOwnerId = (event as any).createdByUserId || (event as any).ownerID || (event as any).organizerId || '';
    return eventOwnerId === userId;
  };

  const hasJoinedEvent = (event: EventType | undefined): boolean => {
    if (!isAuthenticated || !user || !event) return false;
    const userId = currentUserId;
    if (!userId) return false;
    const participatingUsers = (event as any).participatingUsers || [];
    return participatingUsers.includes(userId);
  };

  useEffect(() => {
    const loadAllCategories = async () => {
      try {
        const categories = await fetchCategories();
        const sortedCategories = ['all', ...categories.sort()];
        setAvailableCategories(sortedCategories);
      } catch (error: any) {
        setAvailableCategories(['all']);
      }
    };

    loadAllCategories();
  }, []);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchEventsWithLocation(
          userLocation,
          searchRadius,
          selectedCategory === 'all' ? undefined : selectedCategory,
          'future',
          {
            page: 1,
            limit: 200
          }
        );

        const allEvents = response.events || [];
        let filteredEvents = allEvents;
        if (userLocation && userLocation.lat && userLocation.lng) {
          filteredEvents = allEvents.filter((event: any) => {
            if (!event.coordinates || !event.coordinates.lat || !event.coordinates.lng) {
              return false;
            }
            const distance = calculateDistance(
              userLocation.lat,
              userLocation.lng,
              event.coordinates.lat,
              event.coordinates.lng
            );
            return distance <= searchRadius;
          });
        }

        const filteredTotal = filteredEvents.length;
        const totalFilteredPages = Math.ceil(filteredTotal / eventsPerPage);
        const validPage = Math.max(1, Math.min(currentPage, totalFilteredPages || 1));

        setEvents(filteredEvents);
        setTotalEvents(filteredTotal);
        setTotalPages(totalFilteredPages);

        if (validPage !== currentPage) {
          setCurrentPage(validPage);
        }
      } catch (error: any) {
        setError(error.message || 'Failed to load events');
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, [userLocation, searchRadius, selectedCategory, currentPage, eventsPerPage]);

  useEffect(() => {
    setRadiusDraft(searchRadius);
  }, [searchRadius]);

  useEffect(() => {
    const loadEmailAlertSubscription = async () => {
      if (!isAuthenticated || !currentUserId) {
        setEmailAlertSubscription(null);
        return;
      }

      try {
        setEmailAlertLoading(true);
        setEmailAlertError(null);
        const response = await fetch(buildEventsApiUrl(`/api/email-alerts/subscription?userId=${encodeURIComponent(currentUserId)}`));
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'Failed to load new-event alert status');
        }
        setEmailAlertSubscription(data.subscription || null);
      } catch (subscriptionError: any) {
        setEmailAlertError(subscriptionError.message || 'Failed to load new-event alert status');
      } finally {
        setEmailAlertLoading(false);
      }
    };

    loadEmailAlertSubscription();
  }, [currentUserId, isAuthenticated]);

  const runNearbyAlertSubscribe = useCallback(async (userIdOverride?: string, emailOverride?: string) => {
    const effectiveUserId = userIdOverride || currentUserId;
    const effectiveEmail = emailOverride || (user as any)?.email;
    if (!userLocation || !effectiveUserId || !effectiveEmail) {
      setEmailAlertError('Set your location and make sure your account has an email address first.');
      return;
    }

    try {
      setEmailAlertBusy(true);
      setEmailAlertError(null);
      setEmailAlertMessage(null);
      const response = await fetch(buildEventsApiUrl('/api/email-alerts/subscribe'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: effectiveEmail,
          userId: effectiveUserId,
          location: {
            city: userLocation.city,
            state: userLocation.state,
            lat: userLocation.lat,
            lng: userLocation.lng,
          },
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to subscribe to new-event alerts');
      }
      setEmailAlertSubscription(data.subscription || null);
      setEmailAlertMessage(data.message || 'Confirmation email sent. Check your inbox.');
    } catch (subscribeError: any) {
      try {
        const fallbackResponse = await fetch(buildEventsApiUrl(`/api/email-alerts/subscription?userId=${encodeURIComponent(effectiveUserId)}`));
        const fallbackData = await fallbackResponse.json().catch(() => ({}));
        if (fallbackResponse.ok && fallbackData.subscription) {
          setEmailAlertSubscription(fallbackData.subscription);
          setEmailAlertError('Your alert subscription was saved, but we could not send the confirmation email right now. You can manage it from your profile.');
        } else {
          setEmailAlertError(subscribeError.message || 'Failed to subscribe to new-event alerts');
        }
      } catch {
        setEmailAlertError(subscribeError.message || 'Failed to subscribe to new-event alerts');
      }
    } finally {
      setEmailAlertBusy(false);
    }
  }, [currentUserId, user, userLocation]);

  const handleSubscribeToNearbyAlerts = async () => {
    if (!userLocation) {
      setEmailAlertError('Set your location and make sure your account has an email address first.');
      return;
    }

    if (!currentUserId || !isAuthenticated || !(user as any)?.email) {
      localStorage.setItem('crowdtrades_pending_nearby_alert_signup', '1');
      localStorage.setItem('crowdtrades_post_login_redirect', '/');
      navigate('/login');
      return;
    }

    await runNearbyAlertSubscribe();
  };

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const visibleEvents = useMemo(() => {
    if (!normalizedSearchQuery) return events;

    return events.filter((event) => {
      const haystack = [
        event.eventName,
        (event as any).description,
        event.category,
        event.listingCityName,
        event.listingStateName,
        event.searchItemText,
        ...(event.itemNames || [])
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedSearchQuery);
    });
  }, [events, normalizedSearchQuery]);

  const paginatedVisibleEvents = useMemo(() => {
    const totalVisiblePages = Math.ceil(visibleEvents.length / eventsPerPage);
    const validPage = Math.max(1, Math.min(currentPage, totalVisiblePages || 1));
    const startIdx = (validPage - 1) * eventsPerPage;
    const endIdx = startIdx + eventsPerPage;
    return visibleEvents.slice(startIdx, endIdx);
  }, [visibleEvents, currentPage, eventsPerPage]);

  useEffect(() => {
    const pendingSignup = localStorage.getItem('crowdtrades_pending_nearby_alert_signup');
    if (!pendingSignup || !isAuthenticated || !currentUserId || !(user as any)?.email || !userLocation) {
      return;
    }
    localStorage.removeItem('crowdtrades_pending_nearby_alert_signup');
    runNearbyAlertSubscribe(currentUserId, (user as any)?.email);
  }, [currentUserId, isAuthenticated, user, userLocation, runNearbyAlertSubscribe]);

  const stableEventImageOrder = useCallback((eventId: string, imageUrls: string[]) => {
    const hashString = (value: string) => {
      let hash = 2166136261;
      for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
      }
      return hash >>> 0;
    };

    return [...imageUrls].sort((left, right) => {
      const leftHash = hashString(`${eventId}::${left}`);
      const rightHash = hashString(`${eventId}::${right}`);
      if (leftHash === rightHash) {
        return left.localeCompare(right);
      }
      return leftHash - rightHash;
    });
  }, []);

  useEffect(() => {
    const visibleEventIds = paginatedVisibleEvents
      .map((event) => String(event.id || event.eventID || ''))
      .filter(Boolean);

    if (visibleEventIds.length === 0) {
      setEventCardImages({});
      return;
    }

    let isCancelled = false;

    const loadEventCardImages = async () => {
      const results = await Promise.all(
        paginatedVisibleEvents.map(async (event) => {
          const eventId = String(event.id || event.eventID || '');
          if (!eventId) return [eventId, []] as const;

          try {
            const response = await fetchItemsForEvent(eventId);
            const imageUrls = (response.items || [])
              .flatMap((item: any) => {
                const normalized = normalizeImageUrls(item.images || []);
                if (normalized.length > 0) return normalized;
                return normalizeImageUrls([item.bggImage || item.bggThumbnail].filter(Boolean));
              })
              .filter(Boolean);

            const uniqueUrls = [...new Set(imageUrls)];
            return [eventId, stableEventImageOrder(eventId, uniqueUrls).slice(0, 4)] as const;
          } catch (cardImageError) {
            return [eventId, []] as const;
          }
        })
      );

      if (!isCancelled) {
        setEventCardImages(Object.fromEntries(results));
      }
    };

    loadEventCardImages().catch(() => {});

    return () => {
      isCancelled = true;
    };
  }, [paginatedVisibleEvents, stableEventImageOrder]);

  const handleRadiusChange = (radius: number) => {
    setSearchRadius(radius);
    saveRadiusPreference(radius);
    setCurrentPage(1);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handleEventClick = (event: EventType) => {
    const eventId = event.eventID || event.id || (event as any).eventDocId;
    if (eventId) {
      navigate(`/events/${eventId}`);
    }
  };

  const startItem = ((currentPage - 1) * eventsPerPage) + 1;
  const endItem = Math.min(currentPage * eventsPerPage, totalEvents);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔄</div>
          <div style={{ fontSize: '1.2em', marginBottom: '10px' }}>Loading Crowd Trades...</div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            {userLocation ? 
              `Finding events within ${searchRadius} miles of ${userLocation.city}, ${userLocation.state}...` :
              'Loading all events...'
            }
          </div>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>      
        <Box sx={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
          <div style={{ color: 'red', marginBottom: '10px', fontSize: '1.1em' }}>Error: {error}</div>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
            Make sure backend is running on port 4000
          </div>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{
              backgroundColor: '#667eea',
              '&:hover': { backgroundColor: '#5a67d8' },
              borderRadius: '25px',
              textTransform: 'none',
              fontWeight: '600',
              padding: '10px 24px'
            }}
          >
            Retry
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', py: 4 }}>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {(!emailAlertSubscription || emailAlertSubscription.status !== 'active') && (
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: '700', mb: 1 }}>
                        New event alerts
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        Get alerted when new events appear within 50 miles of your saved location. If you are not logged in yet, we will prompt you to sign in first.
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      onClick={handleSubscribeToNearbyAlerts}
                      disabled={emailAlertBusy || emailAlertLoading || !userLocation}
                      sx={{ textTransform: 'none', fontWeight: '600' }}
                    >
                      {emailAlertBusy
                        ? 'Saving...'
                        : emailAlertSubscription
                          ? (emailAlertSubscription.status === 'active' ? 'Alert Active' : 'Resend Confirmation')
                          : (isAuthenticated ? 'Alert Me' : 'Log in to sign up')}
                    </Button>
                  </Box>

                  {emailAlertError && <Alert severity="error" sx={{ mt: 2 }}>{emailAlertError}</Alert>}
                  {emailAlertMessage && <Alert severity="success" sx={{ mt: 2 }}>{emailAlertMessage}</Alert>}

                  {!userLocation && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Set your location first so we know where to watch for nearby events.
                    </Alert>
                  )}

                  {userLocation && !emailAlertError && !emailAlertMessage && (
                    <Alert severity={emailAlertSubscription?.status === 'active' ? 'success' : 'info'} sx={{ mt: 2 }}>
                      {emailAlertSubscription
                        ? (emailAlertSubscription.status === 'active'
                          ? `Active for ${userLocation.city}, ${userLocation.state} within 50 miles.`
                          : `Pending confirmation for ${userLocation.city}, ${userLocation.state} within 50 miles. Check your inbox.`)
                        : `Currently off for ${userLocation.city}, ${userLocation.state}.`}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid item xs={12}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                cursor: 'pointer',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(102, 126, 234, 0.3)'
                }
              }}
              onClick={() => navigate('/faq')}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: '700', mb: 2 }}>
                  📚 Learn about Crowd Trades
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.95 }}>
                  Discover how Crowd Trades works and start trading with your community
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              {availableCategories.length > 1 && (
                <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="category-select-label">Category</InputLabel>
                  <Select
                    labelId="category-select-label"
                    id="category-select"
                    value={selectedCategory}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    label="Category"
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: '8px'
                    }}
                  >
                    {availableCategories.map(category => (
                      <MenuItem key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Box sx={{ 
                flex: 1,
                minWidth: '300px',
                maxWidth: '400px',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 2,
                py: 2,
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}>
                <StraightenIcon sx={{ color: '#667eea' }} />
                <Typography variant="body2" sx={{ minWidth: 'fit-content', fontWeight: '500' }}>
                  Radius:
                </Typography>
                <Slider
                  value={radiusDraft}
                  onChange={(_, value) => setRadiusDraft(Array.isArray(value) ? value[0] : value)}
                  onChangeCommitted={(_, value) => handleRadiusChange(Array.isArray(value) ? value[0] : value)}
                  min={5}
                  max={200}
                  step={5}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value} miles`}
                  sx={{
                    color: '#667eea',
                    flex: 1,
                    '& .MuiSlider-thumb': {
                      width: 20,
                      height: 20,
                    }
                  }}
                />
                <Typography variant="body2" sx={{ minWidth: 'fit-content', color: '#667eea', fontWeight: '600' }}>
                  {radiusDraft} mi
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={3}>
              {paginatedVisibleEvents.length > 0 ? paginatedVisibleEvents.map((event) => {
                const eventId = String(event.id || event.eventID || '');
                const previewImages = eventCardImages[eventId] || [];
                const joined = hasJoinedEvent(event);
                const owner = isEventOwner(event);

                return (
                  <Grid item xs={12} sm={6} md={4} key={eventId || event.eventName}>
                    <Card
                      data-testid="event-card"
                      sx={{
                        height: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                        },
                        borderRadius: 3,
                        overflow: 'hidden'
                      }}
                      onClick={() => handleEventClick(event)}
                    >
                      {previewImages.length > 0 ? (
                        <Box sx={{ display: 'grid', gridTemplateColumns: previewImages.length > 1 ? 'repeat(2, 1fr)' : '1fr', gap: 0.5, p: 1, backgroundColor: '#f8f9fa' }}>
                          {previewImages.map((imageUrl, index) => (
                            <Box
                              key={`${eventId}-preview-${index}`}
                              component="img"
                              src={imageUrl}
                              alt={`${event.eventName} preview ${index + 1}`}
                              sx={{ width: '100%', height: previewImages.length > 1 ? 96 : 140, objectFit: 'cover', borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)' }}
                            />
                          ))}
                        </Box>
                      ) : null}
                      <CardContent>
                        {owner && <Chip icon={<CheckCircleIcon />} label="My Event" size="small" sx={{ mb: 1, backgroundColor: 'rgba(102, 126, 234, 0.1)', color: '#667eea', fontWeight: '600' }} />}
                        {joined && !owner && <Chip icon={<CheckCircleIcon />} label="Joined" size="small" sx={{ mb: 1, backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#16a34a', fontWeight: '600' }} />}
                        <Typography variant="h6" sx={{ fontWeight: '700', mb: 1 }}>{event.eventName}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <LocationOnIcon sx={{ fontSize: 18, color: '#667eea' }} />
                          <Typography variant="body2" color="text.secondary">{event.listingCityName}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>📅 {formatDate(event.eventDateTime || (event as any).date)}</Typography>
                        <Chip label={event.category} size="small" sx={{ mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">{(event as any).itemCount ?? ((event as any).itemIds?.length ?? (event as any).totalItems ?? 0)} items</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              }) : (
                <Grid item xs={12}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                      <Typography variant="h6" sx={{ mb: 1 }}>No events found</Typography>
                      <Typography variant="body2" color="text.secondary">Try adjusting your search radius or category.</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
              <Button disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>← Previous</Button>
              <Typography variant="body2">Page {currentPage} of {Math.max(totalPages, 1)} ({startItem}-{endItem} of {totalEvents})</Typography>
              <Button disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>Next →</Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3959;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function formatDate(value?: string | null): string {
  if (!value) return 'TBD';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString();
}
